/** Per-folder AI access override within a workspace */
export interface FileOverride {
  id: string
  fileId: string
  principal: string        // 'ai' now; 'user:xxx' future
  permission: 'hidden' | 'r'
}

/** Workspace config as returned by the API */
export interface WorkspaceConfig {
  id: string           // workspace nanoid
  folderId: string     // the .workpapan folder in files tree
  name: string
  path?: string        // VFS path of the .workpapan folder (for tooltip)
  layout: WorkspaceLayout
  links: WorkspaceLink[]
  tabs: WorkspaceTabDef[]
  fileOverrides?: FileOverride[]
}

export interface WorkspaceLink {
  id: string
  targetFolderId: string
  alias: string | null
  sortOrder: number
  permission: 'r' | 'rw'
  /**
   * (#327 Track B) True when this link targets a `.shared`/`.chain` MOUNT rather than
   * a native folder — i.e. the workspace is mount-rooted. Drives the agent-surface
   * suppression: v1 keeps the assistant OFF these, because its sandbox would bind
   * cross-tenant source content. Server-set; the client cannot infer it, since a link
   * carries only an id.
   */
  isMount?: boolean
  /**
   * (#327 B3 lift, step 1) True when this mount resolves write-capable, so the
   * agent WILL bind it and the assistant is offered. A mount link without this is
   * suppressed worker-side, so showing the assistant would advertise something
   * that cannot run. Server-set for the same reason as `isMount` — the client
   * knows only the link id, never the grant behind it.
   */
  agentCapable?: boolean
  /**
   * The owner shared this mount WITHOUT onward-sharing rights, so minting a share
   * link on its root row is refused (`reshare_blocked_by_grant`). Server-set, and it
   * has to be: the explorer has only a link id, and the facts behind a grant are not
   * something a client can infer. The drive reads the same answer for a row through
   * its own listing. Absent on a native link — nothing to refuse.
   */
  reshareBlocked?: boolean
  /**
   * The mount is DEAD — revoked, or the source content is gone. A mint on it is a
   * hard `403 no_file_access`, so the share action is withheld rather than offered
   * and refused. Server-set for the same reason as `reshareBlocked`.
   */
  shareBroken?: boolean
  /** True if the target folder was not found (deleted or cross-tenant) */
  missing?: boolean
  /** VFS path for re-linking (shown in relink dialog) */
  path?: string
  /** If missing but path resolved, this is the suggested new folder ID */
  resolvedId?: string
}

/**
 * (#327 B3) Is there NOTHING in this link set the agent could work on — i.e. is
 * every link a mount the worker will refuse to bind?
 *
 * The suppression this drives must be all-or-nothing, not any-of. An earlier
 * `.some(l => l.isMount && !l.agentCapable)` hid the assistant across a whole
 * workspace the moment ONE mount went dead, so a set of native folders plus one
 * revoked `.shared` lost it entirely — even though the worker drops the dead
 * mount and binds the rest. Worst with a `hidden` placement: an org carving one
 * member out of a mount linked into a shared team workspace cost that member
 * their assistant while colleagues kept theirs, with nothing explaining why.
 *
 * An empty link set is NOT suppressed: the agent always has the workspace's own
 * folder, so a workspace with no links at all still has something to work on.
 */
export function hasNoAgentUsableLinks(links: WorkspaceLink[]): boolean {
  if (links.length === 0) return false
  return links.every(l => l.isMount && !l.agentCapable)
}

export interface WorkspaceTabDef {
  id: string
  fileId: string
  sortOrder: number
}

export interface WorkspaceLayout {
  explorerWidth: number
  chatWidth: number
  /** @deprecated Superseded by explorer/chatCollapsed. Only READ, to migrate
   * a legacy "both hidden" layout on load — never written anymore. */
  panelsHidden?: boolean
  /** Per-panel collapse. Optional so older saved layouts + server route
   * builders that omit them stay valid. */
  explorerCollapsed?: boolean
  chatCollapsed?: boolean
  activeTabIndex: number
}

export function createDefaultLayout(): WorkspaceLayout {
  return {
    explorerWidth: 220,
    chatWidth: 320,
    explorerCollapsed: false,
    chatCollapsed: false,
    activeTabIndex: 0,
  }
}
